import React, { useState, useEffect } from 'react';
import {
  Button,
  TextInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Calendar,
  Popover,
} from '@c2fo/liquidity';
import { CalendarIcon } from '@c2fo/liquidity/icons';
import { format } from 'date-fns';
import { LoadingOverlay } from '../LoadingSpinner';
import { ListViewSwitch } from '../ListViewSwitch';
import { LocationTable } from '@admin-ui-root/src/app/pages/FileDelivery/components/LocationTable';
import { LocationGrid } from '@admin-ui-root/src/app/pages/FileDelivery/components/LocationGrid';
import { useLocationsState } from '../../contexts';
import type { LocationsSortField } from '../../contexts/FileDeliveryContext';
import { NewLocationModal } from '../NewLocationModal';
import { useErrorState } from '../ErrorBanner';
import { SavedSearches } from '../SavedSearches';
import { useSavedSearches } from '../../hooks';
import { Pagination } from '../Pagination';
import { PageHeader } from '../PageHeader';

const FILTER_FIELDS = [
  { value: 'display_name', label: 'Display Name', type: 'text' as const },
  { value: 'name', label: 'Name', type: 'text' as const },
  { value: 'uri', label: 'URI', type: 'text' as const },
  { value: 'create_time', label: 'Created', type: 'date' as const },
  { value: 'update_time', label: 'Updated', type: 'date' as const },
];

const DATE_OPERATORS = [
  { value: '>', label: 'After' },
  { value: '<', label: 'Before' },
  { value: '=', label: 'Equals' },
  { value: 'between', label: 'Between' },
];

const SORT_OPTIONS = [
  { value: 'display_name:asc', label: 'Name (A-Z)' },
  { value: 'display_name:desc', label: 'Name (Z-A)' },
  { value: 'create_time:desc', label: 'Created (Newest)' },
  { value: 'create_time:asc', label: 'Created (Oldest)' },
  { value: 'update_time:desc', label: 'Updated (Newest)' },
  { value: 'update_time:asc', label: 'Updated (Oldest)' },
  { value: 'uri:asc', label: 'URI (A-Z)' },
  { value: 'uri:desc', label: 'URI (Z-A)' },
];

export const LocationsListView: React.FC = () => {
  const [filterField, setFilterField] = useState('display_name');
  const [filterValue, setFilterValue] = useState('');
  const [dateOperator, setDateOperator] = useState('>');
  const [dateValue, setDateValue] = useState<Date | undefined>(undefined);
  const [dateValueEnd, setDateValueEnd] = useState<Date | undefined>(undefined);
  const [isNewLocationModalOpen, setIsNewLocationModalOpen] = useState(false);
  const { addError } = useErrorState();
  const { savedSearches, saveSearch, deleteSearch, isNameTaken } = useSavedSearches('locations');
  const {
    locations,
    isLoading,
    currentPage,
    totalPages,
    totalSize,
    pageSize,
    hasNextPage,
    hasPreviousPage,
    fetchLocations,
    setPageSize,
    setFilter,
    filter,
    setSort,
    sortField,
    sortDirection,
    setViewMode,
    viewMode,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    activeSearchName,
    setActiveSearchName,
  } = useLocationsState();

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleApplyFilter = () => {
    const field = FILTER_FIELDS.find((f) => f.value === filterField);
    let newFilter = '';

    if (field?.type === 'text' && filterValue.trim()) {
      newFilter = `${filterField}:"${filterValue.trim()}"`;
    } else if (field?.type === 'date' && dateValue) {
      const formattedDate = format(dateValue, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      if (dateOperator === 'between' && dateValueEnd) {
        const formattedDateEnd = format(dateValueEnd, "yyyy-MM-dd'T'HH:mm:ss'Z'");
        newFilter = `${filterField} >= "${formattedDate}" AND ${filterField} <= "${formattedDateEnd}"`;
      } else {
        newFilter = `${filterField} ${dateOperator} "${formattedDate}"`;
      }
    }

    setFilter(newFilter || undefined);
    setActiveSearchName(undefined);
  };

  const handleClearFilter = () => {
    setFilterValue('');
    setDateValue(undefined);
    setDateValueEnd(undefined);
    setFilter(undefined);
    setActiveSearchName(undefined);
  };

  useEffect(() => {
    if (filter !== undefined) {
      fetchLocations();
    }
  }, [filter, fetchLocations]);

  const handleFieldChange = (value: string) => {
    setFilterField(value);
    setFilterValue('');
    setDateValue(undefined);
    setDateValueEnd(undefined);
  };

  const selectedField = FILTER_FIELDS.find((f) => f.value === filterField);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Locations"
          actionLabel="New Location"
          onActionClick={() => setIsNewLocationModalOpen(true)}
        />
        <LoadingOverlay message="Loading locations..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader title="Locations" actionLabel="New Location" onActionClick={() => setIsNewLocationModalOpen(true)} />
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Select value={filterField} onValueChange={handleFieldChange}>
              <SelectTrigger className="w-36" size="sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FILTER_FIELDS.map((field) => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedField?.type === 'text' && (
              <TextInput
                placeholder={`Search ${selectedField.label.toLowerCase()}...`}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                className="w-48"
                size="sm"
              />
            )}

            {selectedField?.type === 'date' && (
              <>
                <Select value={dateOperator} onValueChange={setDateOperator}>
                  <SelectTrigger className="w-28" size="sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_OPERATORS.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Popover>
                  <Popover.Trigger asChild>
                    <button
                      type="button"
                      className="w-44 h-9.25 px-3 py-2 rounded bg-white text-sm font-normal border border-gray-300 flex flex-row items-center gap-2 hover:bg-gray-50"
                    >
                      <CalendarIcon className="h-4 w-4 shrink-0" />
                      <span>{dateValue ? format(dateValue, 'MMM d, yyyy') : 'Pick date'}</span>
                    </button>
                  </Popover.Trigger>
                  <Popover.Content variant="light" className="w-auto p-2">
                    <Calendar mode="single" selected={dateValue} onSelect={setDateValue} />
                  </Popover.Content>
                </Popover>

                {dateOperator === 'between' && (
                  <>
                    <span className="text-sm text-gray-500">and</span>
                    <Popover>
                      <Popover.Trigger asChild>
                        <button
                          type="button"
                          className="w-44 h-9.25 px-3 py-2 rounded bg-white text-sm font-normal border border-gray-300 flex flex-row items-center gap-2 hover:bg-gray-50"
                        >
                          <CalendarIcon className="h-4 w-4 shrink-0" />
                          <span>{dateValueEnd ? format(dateValueEnd, 'MMM d, yyyy') : 'Pick date'}</span>
                        </button>
                      </Popover.Trigger>
                      <Popover.Content variant="light" className="w-auto p-2">
                        <Calendar mode="single" selected={dateValueEnd} onSelect={setDateValueEnd} />
                      </Popover.Content>
                    </Popover>
                  </>
                )}
              </>
            )}

            <Button variant="secondary" size="sm" onClick={handleApplyFilter} className="rounded-md">
              Apply
            </Button>
            {filter && (
              <Button variant="tertiary" size="sm" onClick={handleClearFilter} className="rounded-md">
                Clear
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'grid' && (
              <Select
                value={`${sortField}:${sortDirection}`}
                onValueChange={(value) => {
                  const [field, direction] = value.split(':') as [LocationsSortField, 'asc' | 'desc'];
                  setSort(field, direction);
                }}
              >
                <SelectTrigger className="w-44" size="sm">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <ListViewSwitch viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>
        </div>

        <SavedSearches
          searches={savedSearches}
          onApply={(search) => {
            setFilter(search.filter);
            if (search.sortField && search.sortDirection) {
              setSort(search.sortField as LocationsSortField, search.sortDirection);
            }

            setActiveSearchName(search.name);
          }}
          onDelete={deleteSearch}
          onSave={saveSearch}
          currentFilter={filter}
          currentSortField={sortField}
          currentSortDirection={sortDirection}
          isNameTaken={isNameTaken}
          activeSearchName={activeSearchName}
        />

        {filter && (
          <div className="text-sm text-gray-600 bg-gray-100 px-3 py-2 rounded-md">
            <span className="font-medium">Active filter:</span>{' '}
            <code className="bg-gray-200 px-1 rounded">{filter}</code>
          </div>
        )}

        {viewMode === 'list' ? <LocationTable /> : <LocationGrid locations={locations} />}

        <Pagination
          totalSize={totalSize}
          pageSize={pageSize}
          currentPage={currentPage}
          totalPages={totalPages}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onPageSizeChange={setPageSize}
          onFirst={goToFirstPage}
          onPrevious={goToPreviousPage}
          onNext={goToNextPage}
        />
      </div>

      <NewLocationModal
        isOpen={isNewLocationModalOpen}
        onClose={() => setIsNewLocationModalOpen(false)}
        onCreated={() => {
          fetchLocations();
        }}
        onError={(message) => addError({ id: 'create-location-error', message, type: 'error' })}
      />
    </div>
  );
};
